import React, { useEffect, useState } from "react";
import { Box, Flex, Grid, Input, Label, Select, Switch, Textarea } from "theme-ui";

import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { getProviders } from "api/Providers";
import {
  applySurchargeToOpenQuotes,
  clearProviderSurcharge,
  getFuelSurchargeSettings,
  getOpenQuotesWithoutSurcharge,
  setProviderSurcharge,
  updateFuelSurchargeSettings,
  type FuelSurchargeSettings as Settings,
  type OpenQuoteRow,
  type ProviderSurchargeOverride,
} from "api/FuelSurcharge";

/**
 * Company Settings > Fuel Surcharge. Self-contained (own load and saves),
 * like the Leads tab - the page's "Save Company" does not touch it.
 *  1. On/off, company surcharge % (default 5) and company margin % (default 0).
 *  2. Provider overrides: own %, and optionally own margin (blank = company margin).
 *  3. Open quotes priced before the surcharge, with "Apply" - sent or not;
 *     what matters is the surcharge is on the quote when it is accepted.
 * Client waivers are set on the client record.
 */

const card = {
  backgroundColor: "#FFFFFF",
  borderRadius: "6px",
  border: "1px solid",
  borderColor: themevals.colors.inputOutline,
  boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
  marginBottom: "24px",
  padding: "24px",
};
const titleSx = { fontFamily: "Poppins, sans-serif", fontSize: "16px", fontWeight: 600, color: "base_800", marginBottom: "4px" };
const subSx = { fontFamily: "Poppins, sans-serif", fontSize: "13px", color: "base_500", marginBottom: "16px" };
const labelSx = { fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800", marginBottom: "4px", display: "block" };
const inputSx = { fontFamily: "Poppins", fontSize: "13px", borderColor: "#EBEBEB", borderRadius: "6px" };
const cell: React.CSSProperties = { fontFamily: "Poppins", fontSize: 13, padding: "8px 6px", borderBottom: "1px solid #F1EFE8" };
const money = (n: number) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FuelSurchargeSettings: React.FC = () => {
  const toast = useToast();
  const [form, setForm] = useState<Settings | null>(null);
  const [overrides, setOverrides] = useState<ProviderSurchargeOverride[]>([]);
  const [providers, setProviders] = useState<Array<{ id: string; name: string }>>([]);
  const [newOv, setNewOv] = useState<{ providerId: string; percent: string; markupPercent: string }>({ providerId: "", percent: "", markupPercent: "" });
  const [open, setOpen] = useState<OpenQuoteRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const loadOpen = () =>
    getOpenQuotesWithoutSurcharge().then((r: any) => {
      if (!r?.error) setOpen(r.quotes ?? []);
    });

  useEffect(() => {
    getFuelSurchargeSettings().then((r: any) => {
      if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
      setForm(r.settings);
      setOverrides(r.providers ?? []);
    });
    getProviders({ query: { pageNum: 1, recordsPer: 500, sortBy: "name", sortDir: "asc" }, excludeInterceptor: true }).then((r: any) => {
      const items = Array.isArray(r?.items) ? r.items : Array.isArray(r) ? r : [];
      setProviders(items.map((p: any) => ({ id: p.id, name: p.name })));
    });
    loadOpen();
  }, []); // eslint-disable-line

  if (!form) return null;

  const save = async () => {
    setBusy("save");
    const r: any = await updateFuelSurchargeSettings(form);
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    setForm(r);
    toast.add(r.enabled ? `Fuel surcharge on at ${r.percent}%.` : "Fuel surcharge off.", "var(--theme-ui-colors-success)");
    loadOpen();
  };

  const saveOverride = async (providerId: string, percent: string, markupPercent: string) => {
    if (!providerId || percent.trim() === "") return toast.add("Pick a provider and a surcharge %.", "var(--theme-ui-colors-warning)");
    setBusy(`ov-${providerId}`);
    const r: any = await setProviderSurcharge(providerId, { percent: Number(percent), markupPercent: markupPercent.trim() === "" ? null : Number(markupPercent) });
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    setOverrides(r);
    setNewOv({ providerId: "", percent: "", markupPercent: "" });
    toast.add("Provider override saved.", "var(--theme-ui-colors-success)");
    loadOpen();
  };

  const removeOverride = async (providerId: string) => {
    setBusy(`rm-${providerId}`);
    const r: any = await clearProviderSurcharge(providerId);
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    setOverrides(r);
    toast.add("Override removed - that provider uses the company rate.", "var(--theme-ui-colors-success)");
    loadOpen();
  };

  const applyAll = async (ids?: string[]) => {
    setBusy(ids?.length === 1 ? `ap-${ids[0]}` : "apply");
    const r: any = await applySurchargeToOpenQuotes(ids);
    setBusy(null);
    if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
    toast.add(`Fuel surcharge added to ${r.applied} quote${r.applied === 1 ? "" : "s"}${r.failed ? `, ${r.failed} failed` : ""}.`, "var(--theme-ui-colors-success)");
    loadOpen();
  };

  const unused = providers.filter((p) => !overrides.some((o) => o.providerId === p.id));
  const openWithAmount = open.filter((q) => q.wouldAdd > 0);

  return (
    <Box sx={{ padding: "0 30px" }}>
      {/* 1. Company rate */}
      <Box sx={card}>
        <Box sx={titleSx}>Fuel surcharge</Box>
        <Box sx={subSx}>
          Added to every new quote as its own &ldquo;Fuel surcharge&rdquo; line, per provider. The client is charged the provider&rsquo;s % plus your added points, as a % of their price (5% + 2 points shows as &ldquo;Fuel surcharge (7%)&rdquo;). The provider&rsquo;s % of their cost goes on the PO. Discounts never reduce it. Existing quotes only change when you apply it below.
        </Box>
        <Flex sx={{ alignItems: "center", gap: "12px", marginBottom: "18px" }}>
          <Box sx={{ width: "44px" }}>
            <Switch checked={form.enabled} onChange={(e: any) => setForm({ ...form, enabled: e.target.checked })} />
          </Box>
          <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600 }}>Charge a fuel surcharge on new quotes</Box>
        </Flex>
        <Grid columns={[1, 2]} gap="20px" sx={{ maxWidth: 520 }}>
          <Box>
            <Label sx={labelSx}>Provider surcharge (%)</Label>
            <Input type="number" min={0} max={100} step={0.5} value={form.percent} onChange={(e) => setForm({ ...form, percent: Number(e.target.value) })} sx={inputSx} />
          </Box>
          <Box>
            <Label sx={labelSx}>Added for the client (points)</Label>
            <Input type="number" min={0} step={0.5} value={form.markupPercent} onChange={(e) => setForm({ ...form, markupPercent: Number(e.target.value) })} sx={inputSx} />
          </Box>
        </Grid>
        <Box sx={{ marginTop: "18px" }}>
          <Label sx={labelSx}>Quote terms sentence</Label>
          <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "#78716C", marginBottom: "6px" }}>
            Shown where the quote terms use {"{{fuel_surcharge_notice}}"} (Company Settings &rsaquo; Document), only while the surcharge is on and the client is not waived. Clear it to use the default.
          </Box>
          <Textarea rows={3} value={form.notice} onChange={(e: any) => setForm({ ...form, notice: e.target.value })} sx={{ ...inputSx, fontFamily: "Poppins", fontSize: "13px" }} />
        </Box>
        <Flex sx={{ justifyContent: "flex-end", marginTop: "18px" }}>
          <BasicButton type="button" keepText disabled={busy === "save"} onClick={save}>{busy === "save" ? "Saving\u2026" : "Save"}</BasicButton>
        </Flex>
      </Box>

      {/* 2. Provider overrides */}
      <Box sx={card}>
        <Box sx={titleSx}>Provider overrides</Box>
        <Box sx={subSx}>For providers charging a different rate (e.g. 8%). Leave the added points blank to use the company value.</Box>
        <table style={{ width: "100%", borderCollapse: "collapse", maxWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: "left", fontWeight: 600 }}>Provider</th>
              <th style={{ ...cell, textAlign: "left", fontWeight: 600, width: 120 }}>Provider %</th>
              <th style={{ ...cell, textAlign: "left", fontWeight: 600, width: 120 }}>Added pts</th>
              <th style={{ ...cell, width: 170 }} />
            </tr>
          </thead>
          <tbody>
            {overrides.map((o) => (
              <OverrideRow key={o.providerId} o={o} busy={busy} onSave={saveOverride} onRemove={removeOverride} />
            ))}
            <tr>
              <td style={cell}>
                <Select value={newOv.providerId} onChange={(e) => setNewOv({ ...newOv, providerId: e.target.value })} sx={inputSx}>
                  <option value="">{"Add a provider\u2026"}</option>
                  {unused.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
              </td>
              <td style={cell}><Input type="number" min={0} step={0.5} value={newOv.percent} onChange={(e) => setNewOv({ ...newOv, percent: e.target.value })} sx={inputSx} /></td>
              <td style={cell}><Input type="number" min={0} step={0.5} placeholder={`${form.markupPercent}`} value={newOv.markupPercent} onChange={(e) => setNewOv({ ...newOv, markupPercent: e.target.value })} sx={inputSx} /></td>
              <td style={cell}>
                <BasicButton type="button" keepText disabled={!newOv.providerId || busy === `ov-${newOv.providerId}`} onClick={() => saveOverride(newOv.providerId, newOv.percent, newOv.markupPercent)}>Add</BasicButton>
              </td>
            </tr>
          </tbody>
        </table>
      </Box>

      {/* 3. Open quotes */}
      <Box sx={card}>
        <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
          <Box>
            <Box sx={titleSx}>Open quotes without the surcharge</Box>
            <Box sx={{ ...subSx, marginBottom: 0 }}>
              Quotes not yet accepted, priced before the surcharge. Add it so it is on the quote when the client accepts. No email is sent.
            </Box>
          </Box>
          {form.enabled && openWithAmount.length > 0 && (
            <BasicButton type="button" keepText disabled={busy === "apply"} onClick={() => applyAll(openWithAmount.map((q) => q.quoteId))}>
              {busy === "apply" ? "Applying\u2026" : `Apply to all ${openWithAmount.length}`}
            </BasicButton>
          )}
        </Flex>
        {!form.enabled ? (
          <Box sx={{ ...subSx, marginTop: "12px" }}>Turn the surcharge on to apply it to open quotes.</Box>
        ) : open.length === 0 ? (
          <Box sx={{ ...subSx, marginTop: "12px" }}>Every open quote already has the surcharge.</Box>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
            <thead>
              <tr>
                <th style={{ ...cell, textAlign: "left", fontWeight: 600 }}>Quote</th>
                <th style={{ ...cell, textAlign: "left", fontWeight: 600 }}>Client</th>
                <th style={{ ...cell, textAlign: "right", fontWeight: 600 }}>Total</th>
                <th style={{ ...cell, textAlign: "right", fontWeight: 600 }}>Surcharge adds</th>
                <th style={{ ...cell, width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {open.map((q) => (
                <tr key={q.quoteId}>
                  <td style={cell}>
                    <a href={`/quotes/${q.quoteId}`} style={{ color: "#185FA5" }}>GMQ-{String(q.sequence).padStart(5, "0")}{q.majorVersion > 1 ? ` v${q.majorVersion}` : ""}</a>
                    {q.projectName ? <span style={{ color: themevals.colors.dkgrey }}> · {q.projectName}</span> : null}
                  </td>
                  <td style={cell}>{q.clientName ?? "\u2014"}</td>
                  <td style={{ ...cell, textAlign: "right" }}>{money(q.total)}</td>
                  <td style={{ ...cell, textAlign: "right" }}>{q.wouldAdd > 0 ? `+${money(q.wouldAdd)}` : "nothing (no provider cost)"}</td>
                  <td style={cell}>
                    {q.wouldAdd > 0 && (
                      <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === `ap-${q.quoteId}`} onClick={() => applyAll([q.quoteId])}>Apply</BasicButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>
    </Box>
  );
};

const OverrideRow: React.FC<{
  o: ProviderSurchargeOverride;
  busy: string | null;
  onSave: (providerId: string, percent: string, markupPercent: string) => void;
  onRemove: (providerId: string) => void;
}> = ({ o, busy, onSave, onRemove }) => {
  const [percent, setPercent] = useState(String(o.percent));
  const [markup, setMarkup] = useState(o.markupPercent == null ? "" : String(o.markupPercent));
  return (
    <tr>
      <td style={cell}>{o.providerName ?? o.providerId}</td>
      <td style={cell}><Input type="number" min={0} step={0.5} value={percent} onChange={(e) => setPercent(e.target.value)} sx={inputSx} /></td>
      <td style={cell}><Input type="number" min={0} step={0.5} placeholder="company" value={markup} onChange={(e) => setMarkup(e.target.value)} sx={inputSx} /></td>
      <td style={cell}>
        <Flex sx={{ gap: "6px" }}>
          <BasicButton outline scheme="secondary" type="button" keepText disabled={busy === `ov-${o.providerId}`} onClick={() => onSave(o.providerId, percent, markup)}>Save</BasicButton>
          <BasicButton outline scheme="danger" type="button" keepText disabled={busy === `rm-${o.providerId}`} onClick={() => onRemove(o.providerId)}>Remove</BasicButton>
        </Flex>
      </td>
    </tr>
  );
};

export default FuelSurchargeSettings;
